import React, { useState } from 'react';
import { Send, Star, Quote, MessageCircle, Heart } from 'lucide-react';
import api from '../api/client';

const Feedback = ({ feedbackList = [], setFeedbackList }) => {
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState({
    name: '', type: 'Personal Story & Gratitude', rating: 5, message: '', showPublic: true,
  });
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    try {
      const created = await api.createFeedback({
        name: form.name.trim(),
        type: form.type,
        rating: form.rating,
        message: form.message.trim(),
        visible: form.showPublic,
      });
      if (created.visible && setFeedbackList) {
        setFeedbackList((prev) => [created, ...prev]);
      }
      setSubmitted(true);
      setForm({ name: '', type: 'Personal Story & Gratitude', rating: 5, message: '', showPublic: true });
      setTimeout(() => setSubmitted(false), 2500);
    } catch (_e) {
      setSubmitted(false);
    }
  };

  const filtered = feedbackList.filter((w) => {
    if (filter === 'All') return true;
    if (filter === 'Stories') return w.type.toLowerCase().includes('story');
    if (filter === 'Requests') return w.type.toLowerCase().includes('request');
    return true;
  });

  return (
    <section id="feedback" className="scroll-mt-24 py-12 sm:py-20 bg-emerald-50/60">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700"><MessageCircle className="w-3.5 h-3.5" /> Community Voice</span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-emerald-950 mt-1">Feedback &amp; Testimonials</h2>
          <p className="mt-3 text-emerald-900/70 max-w-2xl mx-auto">Your journey matters to us. Share your meditation or affirmation experiences, feature requests, or personal stories with our community.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm">
            <h3 className="font-serif text-xl text-emerald-950 inline-flex items-center gap-2"><Send className="w-4 h-4 text-emerald-700" /> Share Your Feedback</h3>
            <p className="text-sm text-emerald-900/70 mt-1">Send a private note or public review directly to the creator of winwithmeditation.com.</p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="text-xs text-emerald-800 font-medium">Your Name or Alias *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 focus:ring-2 focus:ring-emerald-300 focus:outline-none text-emerald-900" placeholder="e.g. Jessica T." />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-emerald-800 font-medium">Feedback Type *</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 focus:ring-2 focus:ring-emerald-300 focus:outline-none text-emerald-900 bg-white">
                    <option>Personal Story &amp; Gratitude</option>
                    <option>Meditation / Audio Request</option>
                    <option>Website Feature Request</option>
                    <option>General Encouragement</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-emerald-800 font-medium">Experience Rating</label>
                  <div className="mt-1 flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}>
                        <Star className={`w-6 h-6 ${n <= form.rating ? 'fill-amber-500 text-amber-500' : 'text-emerald-200'}`} />
                      </button>
                    ))}
                    <span className="ml-2 text-xs text-emerald-800">{form.rating}.0 - {form.rating === 5 ? 'Excellent' : form.rating >= 3 ? 'Good' : 'Okay'}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs text-emerald-800 font-medium">Your Thoughts &amp; Message *</label>
                <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 focus:ring-2 focus:ring-emerald-300 focus:outline-none text-emerald-900" placeholder="Write your thoughts or request here…" />
              </div>
              <label className="flex items-start gap-2 text-xs text-emerald-800">
                <input type="checkbox" checked={form.showPublic} onChange={(e) => setForm({ ...form, showPublic: e.target.checked })} className="mt-0.5 accent-emerald-700" />
                Allow showing this testimonial on the public Community Wall
              </label>
              <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-md shadow-emerald-200">
                <Send className="w-4 h-4" /> {submitted ? 'Thank you — Feedback shared!' : 'Submit Feedback'}
              </button>
            </form>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-emerald-950 inline-flex items-center gap-2"><Quote className="w-4 h-4 text-emerald-700" /> Community Wall ({filtered.length})</h3>
              <div className="flex items-center gap-1 p-1 rounded-full bg-emerald-50 border border-emerald-100">
                {['All', 'Stories', 'Requests'].map((f) => (
                  <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-xs ${filter === f ? 'bg-emerald-700 text-white' : 'text-emerald-800 hover:bg-white'}`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="mt-4 space-y-4 max-h-[520px] overflow-y-auto pr-1">
              {filtered.length === 0 && (
                <div className="text-sm text-emerald-800/70 py-10 text-center">No entries yet — be the first to share.</div>
              )}
              {filtered.map((t) => (
                <div key={t.id} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-700 text-white flex items-center justify-center font-semibold">{t.name.charAt(0)}</div>
                      <div>
                        <div className="font-medium text-emerald-950 text-sm">{t.name}</div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < t.rating ? 'fill-amber-500 text-amber-500' : 'text-emerald-200'}`} />)}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest rounded-full bg-white border border-emerald-200 px-2 py-1 text-emerald-800">{t.type}</span>
                  </div>
                  <p className="mt-3 text-sm text-emerald-900/90 leading-relaxed">{t.message}</p>
                  {t.reply && (
                    <div className="mt-3 rounded-lg bg-white border-l-4 border-emerald-500 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1 inline-flex items-center gap-1"><Heart className="w-3 h-3 fill-rose-400 text-rose-400" /> Reply from the Creator · {t.replyDate}</div>
                      <div className="text-sm text-emerald-900/90">{t.reply}</div>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between text-[11px] text-emerald-700">
                    <span>Shared on {t.date}</span>
                    <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> With love</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Feedback;
